import { useAgentVisibility } from '@/entities/agent';
import { useCurrentUser, usePermissions } from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { IQ_TOOL_ID } from '../managedTools';

export interface IqAllowed {
  isAllowed: boolean;
  isLoading: boolean;
}

/**
 * The single gate for IQ access: every surface reads this hook so the permission
 * and the per-workspace ACL cannot drift into disagreeing conditions — a
 * permission alone admits a user into a workspace IQ was never switched on for.
 * IQ has no rollout flag, so unlike Content Studio there is no third condition.
 * `isLoading` is surfaced separately from `isAllowed` so a consumer can hold a
 * tile back rather than flashing it while the ACL check is in flight.
 */
export const useIqAllowed = (): IqAllowed => {
  const { canAccessIq } = usePermissions();
  const workspaceId = useCurrentWorkspaceId();
  // Non-throwing read, unlike `getCurrentOrganizationId`: this hook feeds
  // surfaces that mount before the user store fills. The fallback never reaches
  // a request, since a workspace only resolves once that store has a user.
  const organizationId = useCurrentUser()?.organization.id;
  // The ACL can only narrow a granted permission, never widen a missing one, so
  // asking without the permission would stall a consumer's gate on a known answer.
  const gateApplies = canAccessIq && !!workspaceId;
  const { isAgentEnabled, isLoading } = useAgentVisibility(gateApplies ? [IQ_TOOL_ID] : [], {
    workspaceId,
    organizationId: organizationId ?? '',
  });

  return {
    isAllowed: gateApplies && isAgentEnabled(IQ_TOOL_ID),
    isLoading: gateApplies && isLoading,
  };
};
